import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Video } from "@/types/video";
import { formatViews } from "@/utils/format";

interface VideoCarouselProps {
  videos: Video[];
  categoryNameMap: Record<string, string>;
}

export default function VideoCarousel({ videos, categoryNameMap }: VideoCarouselProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const count = videos.length;

  useEffect(() => {
    if (paused || count <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % count);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [paused, count]);

  const goTo = (next: number) => setIndex((next + count) % count);
  const prev = () => goTo(index - 1);
  const next = () => goTo(index + 1);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 45) {
      if (delta < 0) next();
      else prev();
    }
    touchStartX.current = null;
  };

  if (count === 0) return null;

  return (
    <section
      className="relative w-full overflow-hidden rounded-lg bg-foreground-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      aria-roledescription="carousel"
      aria-label="焦点视频轮播"
    >
      <div className="relative h-[260px] w-full sm:h-[360px] lg:h-[440px]">
        {videos.map((video, i) => (
          <div
            key={video.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
            aria-hidden={i !== index}
          >
            <img
              src={video.cover}
              alt={`${video.title} 封面图`}
              title={`${video.title} - 黑料视频`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/95 via-foreground-950/55 to-foreground-950/20"></div>
            <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-8 lg:p-10">
              <div className="w-full max-w-2xl">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <span className="flex items-center gap-1 rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
                    <i className="ri-fire-fill"></i>
                    焦点
                  </span>
                  <span className="rounded-full bg-background-50/15 px-3 py-1 text-xs font-medium text-background-100 backdrop-blur">
                    {categoryNameMap[video.categoryId] ?? "娱乐资讯"}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-background-300">
                    <i className="ri-play-circle-line"></i>
                    {formatViews(video.views)} 次播放
                  </span>
                </div>
                <h2 className="line-clamp-2 text-lg font-black leading-snug text-background-50 md:text-2xl lg:text-3xl">
                  <Link to={`/detail/${video.id}`} className="hover:text-accent-300 cursor-pointer">
                    {video.title}
                  </Link>
                </h2>
                <p className="mt-3 line-clamp-2 hidden max-w-xl text-sm leading-relaxed text-background-200 sm:block">
                  {video.excerpt}
                </p>
                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <Link
                    to={`/detail/${video.id}`}
                    className="flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-play-fill"></i>
                    立即观看
                  </Link>
                  <Link
                    to={`/category/${video.categoryId}`}
                    className="flex items-center gap-2 rounded-md border border-background-50/30 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10 cursor-pointer whitespace-nowrap"
                  >
                    更多同类
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <button
        type="button"
        onClick={prev}
        aria-label="上一张"
        className="absolute left-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-foreground-950/45 text-background-50 backdrop-blur transition-colors hover:bg-foreground-950/75 cursor-pointer md:left-6 md:h-10 md:w-10"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        type="button"
        onClick={next}
        aria-label="下一张"
        className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-foreground-950/45 text-background-50 backdrop-blur transition-colors hover:bg-foreground-950/75 cursor-pointer md:right-6 md:h-10 md:w-10"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      <div className="absolute bottom-4 right-5 flex items-center gap-2 md:bottom-6 md:right-10">
        {videos.map((video, i) => (
          <button
            key={video.id}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`切换到第 ${i + 1} 张`}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-6 bg-accent-400" : "w-1.5 bg-background-50/50 hover:bg-background-50/80"
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}